/**
 * Governance — bucket layout (DC-1645, tightened again by DC-1646).
 *
 * Governed evidence only:
 * - Documents (Governance documents — the landing tab)
 * - Standards (Interoperability standards)
 * - Recommendations
 * - Evidence Library
 *
 * Moved out by DC-1645: Domains + AI Preferences → Settings; Interviews →
 * People. FAIR Personas (the single persona surface since DC-1648) lives
 * under People too — old governance URLs redirect. Moved out by DC-1646:
 * the Execution Risk Dashboard — a readiness readout, not governed
 * evidence — now lives at Readiness → Organizational (old URL redirects).
 *
 * Renders standalone under the One Compass shell (extracted from the Settings
 * chrome). The `settings-page` wrapper is kept so leaf views keep resolving
 * the light-theme variable scope settings.css pins (DC-1602 lesson).
 *
 * @version 2.1.0
 * @date 2026-06-11
 */

import React from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { FileText, Scale, Lightbulb, Library, Quote } from 'lucide-react';
import { OrgFairyChat } from '../../components/OrgFairyChat';
import './governance-layout.css';

export const GovernanceLayout: React.FC = () => {
  const navigate = useNavigate();

  const handleFairyNavigate = (path: string) => {
    navigate(path);
  };

  return (
    <div className="settings-page">
      <header className="bucket-header">
        <h1>Governance</h1>
        <p className="bucket-subtitle">
          The governed evidence — policy documents, standards, recommendations, and the evidence
          library behind the Governed leg of the assessment. The organizational readiness readout
          lives under Readiness.
        </p>
      </header>

      <div className="governance-layout">
        {/* Governance Sub-Navigation */}
        <nav className="governance-subnav" role="navigation" aria-label="Governance navigation">
          <NavLink
            to="/settings/governance/documents"
            className={({ isActive }) => `governance-tab ${isActive ? 'active' : ''}`}
          >
            <FileText className="tab-icon" size={16} aria-hidden="true" />
            <span className="tab-label">Documents</span>
          </NavLink>
          <NavLink
            to="/settings/governance/citation-mappings"
            className={({ isActive }) => `governance-tab ${isActive ? 'active' : ''}`}
          >
            <Quote className="tab-icon" size={16} aria-hidden="true" />
            <span className="tab-label">Citation Mappings</span>
          </NavLink>
          <NavLink
            to="/settings/governance/standards"
            className={({ isActive }) => `governance-tab ${isActive ? 'active' : ''}`}
          >
            <Scale className="tab-icon" size={16} aria-hidden="true" />
            <span className="tab-label">Standards</span>
          </NavLink>
          <NavLink
            to="/settings/governance/recommendations"
            className={({ isActive }) => `governance-tab ${isActive ? 'active' : ''}`}
          >
            <Lightbulb className="tab-icon" size={16} aria-hidden="true" />
            <span className="tab-label">Recommendations</span>
          </NavLink>
          <NavLink
            to="/settings/governance/evidence-library"
            className={({ isActive }) => `governance-tab ${isActive ? 'active' : ''}`}
          >
            <Library className="tab-icon" size={16} aria-hidden="true" />
            <span className="tab-label">Evidence Library</span>
          </NavLink>
        </nav>

        {/* Content Area - Router handles rendering via Outlet */}
        <div className="governance-content">
          <Outlet />
        </div>
      </div>

      {/* DC-408: Org-level FAIRy Chat */}
      <OrgFairyChat onNavigate={handleFairyNavigate} />
    </div>
  );
};

export default GovernanceLayout;
